import React from "react";
import { motion } from "framer-motion";

const education = [
  {
    period: "2020 - 2023",
    title: "Bachelor's in Computer Applications",
    description:
      "Built a solid full stack foundation at Patliputra University across frontend, backend, and database systems alongside core web development fundamentals.",
  },
  {
    period: "2023 - 2025",
    title: "Master's in Computer Applications",
    description:
      "Completed an MCA at Jain University with a focus on full stack development, scalable applications, and user-centred product building.",
  },
];

const experience = [
  {
    period: "Jan 2024 - Jul 2024",
    title: "Frontend Intern - PureCode",
    description:
      "Worked in Hyderabad on frontend design, reusable React components, UI testing, and authentication flows across the stack.",
    highlights: [
      "Built a reusable React component library that kept the interface consistent and sped up new-feature work.",
      "Supported web scraping and data-extraction pipelines feeding thousands of product records into development and content workflows.",
    ],
  },
  {
    period: "Aug 2024 - Present",
    title: "Intern to Full Stack Developer - Vadhando Easybiznus Private Limited",
    description:
      "Joined as an intern and stepped up to Full Stack Developer within a month. Since then I have owned features across live web and mobile products — commerce logic, payment workflows, location intelligence, and release operations.",
    highlights: [
      "Designed an idempotent Shopify webhook pipeline with duplicate-event handling and database-level locking, lifting order-event reliability to around 99% across multiple stores.",
      "Implemented end-to-end Razorpay flows — order creation, signature verification, refunds, and capture webhooks — clearing a few hundred B2B and B2C payments a week.",
      "Built a cron-driven, multi-store batch processor with exponential-backoff retries, saving a few hours of manual reconciliation every week.",
      "Developed multi-vendor order-splitting and credit-chain logic handling roughly 50 orders a day across 10+ vendors for shared-inventory fulfillment.",
      "Engineered a PostgreSQL-backed job queue (Node.js EventEmitters, serial locking, retry handling) processing about 200 background jobs a day and cutting failed jobs by roughly two-thirds.",
      "Created Decentro KYC onboarding — Aadhaar, PAN, GST, and bank verification — with admin approvals and Firebase push, cutting merchant sign-up time roughly in half.",
      "Shipped vendor bulk product upload (XLSX/CSV) with async processing, automated SKU generation, and catalog approval handling a few hundred SKUs per batch.",
      "Integrated GoSwift and Shiprocket Checkout for logistics; ran AWS/EC2 setup, EventBridge scheduling, Google Play releases, and on-call troubleshooting.",
    ],
  },
];

const TimelineColumn = ({ kicker, title, items }) => (
  <div>
    <p className="section-kicker">{kicker}</p>
    <h2 className="mt-6 font-['Sora'] text-3xl leading-tight text-slate-50 sm:text-4xl">
      {title}
    </h2>

    <div className="mt-10 space-y-10">
      {items.map((item) => (
        <div key={item.title} className="timeline-item relative pl-8">
          <span className="timeline-line" aria-hidden="true" />
          <p className="text-xs uppercase tracking-[0.24em] text-[#f59e0b]">
            {item.period}
          </p>
          <h3 className="mt-3 font-['Sora'] text-xl text-slate-100">
            {item.title}
          </h3>
          <p className="mt-4 max-w-xl text-sm leading-7 text-slate-400 sm:text-base">
            {item.description}
          </p>
          {item.highlights ? (
            <ul className="mt-5 max-w-xl list-disc space-y-3 pl-5 text-sm leading-7 text-slate-400 sm:text-base">
              {item.highlights.map((highlight) => (
                <li key={highlight}>{highlight}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </div>
  </div>
);

const ExperienceEducation = () => {
  return (
    <section className="section-frame pt-2 sm:pt-8">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="grid gap-16 border-y border-white/8 py-16 lg:grid-cols-2 lg:gap-24"
      >
        <TimelineColumn
          kicker="Education"
          title="A steady academic path into full stack development."
          items={education}
        />
        <TimelineColumn
          kicker="Experience"
          title="Practical experience across design, testing, deployment, and production product delivery."
          items={experience}
        />
      </motion.div>
    </section>
  );
};

export default ExperienceEducation;
